import React from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';

import Translate from '@hydra/common/components/i18n';
import Criterium from '@hydra/common/criterium/Criterium';
import SafeAnchor from '@hydra/common/components/SafeAnchor';
import CriteriumTable from '@hydra/common/criterium/CriteriumTable';
import CriteriumIconAndText from '@hydra/common/criterium/CriteriumIconAndText';

const useStyles = makeStyles(theme => ({
  link: {
    marginBottom: theme.spacing(1)
  },
  table: {
    marginBottom: theme.spacing(2)
  }
}));

const SitemapCount = ({ numberSitemaps, numberUrls }) => (
  <Translate
    content={numberSitemaps > 3 ? 'sitemaps_found_more_than' : 'sitemaps_found'}
    with={{ numberSitemaps, numberUrls }}
  />
);

function getRows (messages) {
  return messages.slice(0, 3).map(({ type, details }, key) => {
    let content;
    if (type.toLowerCase() === 'invalidtagvalue') {
      content = `sitemaps_validity_invalidtagvalue_${details.tag}`;
    } else if (type.toLowerCase() === 'invalidxmltag') {
      content = `sitemaps_validity_invalidxmltag${details.parent ? '_parent' : ''}`;
    } else {
      content = `sitemaps_validity_${type.toLowerCase()}`;
    }
    return [
      <Translate
        content={content}
        with={{ ...details }}
        key={`${type.toLowerCase()}_${key}`}
      />
    ];
  });
}

export const SitemapsValidity = ({ criterium }) => {
  const classes = useStyles();
  const { data } = criterium;

  const content = (
    <>
      {data.numberSitemaps && <SitemapCount numberSitemaps={data.numberSitemaps} numberUrls={data.numberUrls} />}
      {
        Object.keys(data.issues).reduce(
          (result, type) => {
            const valid = data.issues[type] && data.issues[type].length === 0;
            const text = <Translate content={`sitemaps_validity_${valid}_${type.toLowerCase()}`} key='sitemaps_validity' />;
            result.push(<CriteriumIconAndText key={type} success={valid} text={text} />);
            result.push(
              ...data.issues[type].map(({ url, messages }, key) => {
                return (
                  <React.Fragment key={`${type}_${key}`}>
                    <div className={classes.link}>
                      <SafeAnchor href={url} rel='nofollow noopener noreferrer' target='_blank'>{url}</SafeAnchor>
                    </div>
                    {messages && messages.length > 0 && (
                      <CriteriumTable
                        head={[<Translate content='sitemaps_validity_issue' key='sitemaps_validity_issue' />]}
                        rows={getRows(messages)}
                        striped
                        className={classes.table}
                        showAmount={3}
                      />
                    )}
                  </React.Fragment>
                );
              })
            );
            return result;
          },
          []
        )
      }
    </>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

SitemapsValidity.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.shape({
      issues: PropTypes.shape({
        maxSize: PropTypes.array,
        tooManyUrls: PropTypes.array,
        invalidStructure: PropTypes.array,
        notAccessible: PropTypes.array,
        wrongFormat: PropTypes.array
      }),
      numberSitemaps: PropTypes.number,
      numberUrls: PropTypes.number
    })
  })
};

export default React.memo(SitemapsValidity);
